import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useDeleteExercise from "../../hooks/api/exercise/useDeleteExercise";
import useGetLearningPathExamsPaginated from "../../hooks/api/learning-path/useGetLearningPathLessonExamsPaginated";
import { ExerciseSummary } from "../../models/Exercise";
import GenericTable from "./GenericTable";

type Props = {
  learningPathId: string;
  lessonId: string;
};

export default function ExercisesTable({ learningPathId, lessonId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data: exercises,
    isPending,
    isError,
    mutateAsync: fetchExercises,
    variables,
  } = useGetLearningPathExamsPaginated(learningPathId, lessonId);
  const { mutateAsync: deleteExercise, isPending: isDeletionPending } =
    useDeleteExercise();

  const onDeleteConfirm = useCallback(
    (exercise: ExerciseSummary) => {
      deleteExercise({ exerciseId: exercise.id! }).finally(() =>
        fetchExercises({ page: variables?.page ?? 0 }),
      );
    },
    [deleteExercise, fetchExercises, variables?.page],
  );

  return (
    <GenericTable<ExerciseSummary>
      items={exercises?.content ?? []}
      headers={[t("forms.name"), t("forms.description")]}
      rowExtractors={[
        (exercise) => exercise.name,
        (exercise) => exercise.description,
      ]}
      onEditClick={(exercise) => navigate(`./exercises/${exercise.id}`)}
      onDeleteConfirm={onDeleteConfirm}
      isDeletionPending={isDeletionPending}
      isLoading={isPending}
      isError={isError}
      refetch={fetchExercises}
      pagination={exercises?.page}
    />
  );
}
